import React, { useCallback, useEffect, useImperativeHandle, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

function getClampedStyle(lines) {
  return {
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: String(lines),
  };
}

const RESIZE_DEBOUNCE_DELAY = 50;

// inspired by https://www.npmjs.com/package/use-line-clamp
export function useLineClamp(ref, { lines }) {
  const [isClamped, setIsClamped] = useState(true);

  const checkIsClamped = useCallback(() => {
    if (!ref.current) return;

    const clampedStyles = getClampedStyle(lines);
    const clampingProps = Object.keys(clampedStyles);

    const initialStyles = clampingProps.reduce((styles, prop) => ({
      ...styles,
      [prop]: ref.current.style[prop],
    }), {});

    clampingProps.forEach(prop => ref.current.style[prop] = clampedStyles[prop]);

    setIsClamped(ref.current.clientHeight !== ref.current.scrollHeight);

    clampingProps.forEach(prop => ref.current.style[prop] = initialStyles[prop]);
  }, [ref, lines]);

  useEffect(() => {
    checkIsClamped();

    const debouncedCheck = debounce(checkIsClamped, RESIZE_DEBOUNCE_DELAY);

    window.addEventListener('resize', debouncedCheck);

    return () => window.removeEventListener('resize', debouncedCheck);
  }, [checkIsClamped]);

  return [isClamped, checkIsClamped];
}

export default function TextCollapser({ children, lines, expanded, onTruncate, truncateRef, ...rest }) {
  const containerRef = useRef(null);

  const [isClamped, checkIsClamped] = useLineClamp(containerRef, { lines });

  useImperativeHandle(truncateRef, () => ({
    onResize: checkIsClamped,
  }), [checkIsClamped]);

  useEffect(() => {
    if (onTruncate) {
      onTruncate(isClamped);
    }
  }, [onTruncate, isClamped]);

  return (
    <div {...rest} ref={containerRef} style={expanded ? null : getClampedStyle(lines)}>
      {children}
    </div>
  );
}

TextCollapser.propTypes = {
  children  : PropTypes.node.isRequired,
  lines     : PropTypes.number.isRequired,
  expanded  : PropTypes.bool,
  // ellipsis  : PropTypes.node,
  onTruncate: PropTypes.func,

  // https://stackoverflow.com/a/51127130
  truncateRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
};
